import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Package, Truck, Hammer } from "lucide-react";
import { http, inr, inr2 } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";

export default function OrderConfirmed() {
  const { orderNumber } = useParams();
  const { data: o, isLoading, isError } = useQuery({ queryKey: ["order", orderNumber], queryFn: () => http.get(`/orders/${orderNumber}`).then((r) => r.data) });

  if (isLoading) return <div className="container-hw py-16"><Skeleton className="h-96" /></div>;
  if (isError) return <div className="container-hw py-24 text-center"><h1 className="text-4xl">Order not found.</h1><Link to="/" className="btn-sheesham mt-8">Go home</Link></div>;

  return (
    <div data-testid="order-confirmed" className="container-hw max-w-4xl py-14 lg:py-20">
      <CheckCircle2 className="h-12 w-12 text-neem dark:text-jute" />
      <p className="eyebrow mt-6">Order placed</p>
      <h1 className="mt-3 text-4xl sm:text-5xl">Thank you, {o.address.full_name.split(" ")[0]}. Your box is on its way soon.</h1>
      <p className="mt-4 text-muted-foreground">Order <b data-testid="order-number" className="text-foreground">{o.order_number}</b>. We've noted {o.address.email} for updates.</p>

      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        {[[Package, "Packed", "Within 1 to 2 working days"], [Truck, "Delivered free", `${o.eta.from_date} to ${o.eta.to_date}`], [Hammer, "Build together", "Keep one evening free"]].map(([I, t, d]) => (
          <div key={t} className="rounded-md border border-border bg-card p-5"><I className="h-5 w-5 text-sheesham dark:text-brass" /><p className="mt-3 font-display text-xl">{t}</p><p className="mt-1 text-sm text-muted-foreground">{d}</p></div>
        ))}
      </div>

      <div className="mt-10 grid gap-8 rounded-md border border-border bg-card p-6 sm:grid-cols-2">
        <ul className="space-y-3">
          {o.items.map((i) => (
            <li key={`${i.slug}${i.variant || ""}`} className="flex items-center gap-3 text-sm"><img src={i.image} alt="" className="h-12 w-12 rounded object-cover" /><span className="flex-1">{i.name}{i.variant_name && ` (${i.variant_name})`} × {i.qty}</span><span className="font-semibold">{inr(i.line_total)}</span></li>
          ))}
        </ul>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(o.subtotal)}</span></div>
          {o.discount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Coupon {o.coupon}</span><span>-{inr(o.discount)}</span></div>}
          {o.tax > 0 && <div className="flex justify-between"><span className="text-muted-foreground">CGST 9%</span><span>{inr2(o.tax / 2)}</span></div>}
          {o.tax > 0 && <div data-testid="order-tax" className="flex justify-between"><span className="text-muted-foreground">SGST 9%</span><span>{inr2(o.tax / 2)}</span></div>}
          <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>Free</span></div>
          <div className="flex justify-between border-t border-border pt-2 text-lg"><span>To pay on delivery</span><span data-testid="order-total" className="font-semibold tabular-nums">{inr2(o.total)}</span></div>
          <p className="pt-3 text-muted-foreground">{o.address.line1}{o.address.line2 && `, ${o.address.line2}`}, {o.address.city}, {o.address.state} {o.address.pincode}. +91 {o.address.phone}</p>
        </div>
      </div>
      <Link to="/shop" data-testid="continue-shopping-button" className="btn-sheesham mt-10">Keep browsing</Link>
    </div>
  );
}
